/**
 * @license
 * @copyright Copyright Motili Inc., 2026 All Rights Reserved
 */

import {
    createAsyncThunk,
    createSelector,
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit';
import forEach from 'lodash/forEach';
import { compare } from 'admin/services/AdminOrdersService';
import { getDefaultSecondaryOrderCancelReason } from 'common/utils/order-utils';
import { Product } from 'common/types';
import {
    ACTIONS,
    Adjustment,
    FULFILLMENT_TYPE,
    MotiliLineItem,
    ReasonSelection,
    ReconcileOptions,
    ResolveValues,
    VendorLineItem,
} from './types';

export interface ModifyOrderState {
    initialized: boolean;
    options?: {
        fulfillmentDetailsDiscrepancies: boolean;
        lineItemDiscrepancies: boolean;
        motiliLineItems: MotiliLineItem[];
        vendorLineItems: VendorLineItem[];
    };

    modifyAction?: ACTIONS;
    reason?: string;
    reasonSelection?: ReasonSelection;
    secondaryReasonSelection?: ReasonSelection;

    fulfillmentType?: FULFILLMENT_TYPE;
    motiliLineItems: MotiliLineItem[];
    vendorLineItems: VendorLineItem[];

    adjustReason?: string;
    adjustReasonNote?: string;
}

const initialState: ModifyOrderState = {
    initialized: false,
    fulfillmentType: 'motili',
    motiliLineItems: [],
    vendorLineItems: [],
};

export const fetchCompareOrder = createAsyncThunk(
    'adminModifyOrder/compare',
    async (orderId: number) => {
        const reconciliationOptions: ReconcileOptions = (await compare(
            orderId
        )) as ReconcileOptions;
        const motiliLineItems: MotiliLineItem[] = [];
        const vendorLineItems: VendorLineItem[] = [];
        forEach(reconciliationOptions.lineItems, (lineItem, index) => {
            motiliLineItems.push({
                ...lineItem.motili,
                rowId: index + 1,
                motili: !!lineItem.motili,
                vendor: !!lineItem.vendor,
            });
            vendorLineItems.push({
                ...lineItem.vendor,
                rowId: index + 1,
                motili: !!lineItem.motili,
                vendor: !!lineItem.vendor,
            });
        });
        return {
            motiliLineItems,
            vendorLineItems,
            fulfillmentDetailsDiscrepancies:
                !reconciliationOptions.doFulfillmentDetailsMatch,
            lineItemDiscrepancies: !reconciliationOptions.doLineItemsMatch,
        };
    }
);

export const modifyOrderSlice = createSlice({
    name: 'adminModifyOrder',
    initialState,
    reducers: {
        resetState: () => initialState,
        modifyChangeAction: (state, action: PayloadAction<ACTIONS>) => {
            state.modifyAction = action.payload;
            state.reason = '';
            state.reasonSelection = undefined;
            state.secondaryReasonSelection = undefined;
        },
        reasonChangeAction: (state, action: PayloadAction<string>) => {
            state.reason = action.payload;
        },
        reasonSelectionChangeAction: (
            state,
            action: PayloadAction<ReasonSelection | undefined>
        ) => {
            state.reasonSelection = action.payload;
            state.secondaryReasonSelection =
                getDefaultSecondaryOrderCancelReason(action.payload);
        },
        secondaryReasonSelectionChangeAction: (
            state,
            action: PayloadAction<ReasonSelection | undefined>
        ) => {
            state.secondaryReasonSelection = action.payload;
        },
        setFulfillmentTypeAction: (
            state,
            action: PayloadAction<FULFILLMENT_TYPE | undefined>
        ) => {
            state.fulfillmentType = action.payload;
        },
        resetEditingLineItems: state => {
            state.motiliLineItems = state.options?.motiliLineItems || [];
            state.vendorLineItems = state.options?.vendorLineItems || [];
        },
        deleteLineItemAction: (state, action: PayloadAction<number>) => {
            const rowId = action.payload;
            state.motiliLineItems = state.motiliLineItems.map(item =>
                item.rowId === rowId
                    ? {
                          ...item,
                          resolved: { by: 'delete', revertIds: [rowId] },
                      }
                    : item
            );
            state.vendorLineItems = state.vendorLineItems.map(item =>
                item.rowId === rowId
                    ? {
                          ...item,
                          resolved: { by: 'delete' },
                      }
                    : item
            );
        },
        revertLineItemsAction: (state, action: PayloadAction<number[]>) => {
            const revertIds = action.payload;
            // revert current items
            state.motiliLineItems = state.motiliLineItems.map(item =>
                revertIds.includes(item.rowId)
                    ? state.options?.motiliLineItems.find(
                          orig => orig.rowId === item.rowId
                      ) || item
                    : item
            );
            state.vendorLineItems = state.vendorLineItems.map(item =>
                revertIds.includes(item.rowId)
                    ? state.options?.vendorLineItems.find(
                          orig => orig.rowId === item.rowId
                      ) || item
                    : item
            );
            // add back for the item was removed by change/sub to the list
            revertIds.forEach(rowId => {
                const motiliItem = state.options?.motiliLineItems.find(
                    item => item.rowId === rowId
                );
                if (
                    motiliItem &&
                    !state.motiliLineItems.find(item => item.rowId === rowId)
                ) {
                    state.motiliLineItems.push(motiliItem);
                }
                const vendorItem = state.options?.vendorLineItems.find(
                    item => item.rowId === rowId
                );
                if (
                    vendorItem &&
                    !state.vendorLineItems.find(item => item.rowId === rowId)
                ) {
                    state.vendorLineItems.push(vendorItem);
                }
            });
            // sort items by rowId
            state.motiliLineItems = state.motiliLineItems.sort(
                (a, b) => a.rowId - b.rowId
            );
            state.vendorLineItems = state.vendorLineItems.sort(
                (a, b) => a.rowId - b.rowId
            );
        },
        selectProductAction: (
            state,
            action: PayloadAction<{ rowId: number; productId?: number }>
        ) => {
            const { rowId, productId } = action.payload;
            state.motiliLineItems = state.motiliLineItems.map(item =>
                item.rowId === rowId
                    ? {
                          ...item,
                          selectProductId: productId,
                      }
                    : item
            );
        },
        addProductAction: (
            state,
            action: PayloadAction<{ rowId: number; product: Product }>
        ) => {
            const { rowId, product } = action.payload;
            const vendorItem = state.vendorLineItems.find(
                item => item.rowId === rowId
            );
            if (!vendorItem) return;
            state.motiliLineItems = state.motiliLineItems.map(item =>
                item.rowId === rowId
                    ? {
                          ...item,
                          ...product,
                          id: rowId,
                          quantity:
                              vendorItem.vendorQuantityOrdered ||
                              vendorItem.quantity ||
                              1,
                          cost: {
                              ...item.cost,
                              ...product.cost,
                          },
                          price: {
                              ...item.price,
                              ...product.price,
                          },
                          resolved: {
                              by: 'add',
                              sku: product.sku,
                              quantity:
                                  vendorItem.vendorQuantityOrdered ||
                                  vendorItem.quantity ||
                                  1,
                              revertIds: [rowId],
                          },
                      }
                    : item
            );
            state.vendorLineItems = state.vendorLineItems.map(item =>
                item.rowId === rowId
                    ? {
                          ...item,
                          resolved: { by: 'add' },
                      }
                    : item
            );
        },
        matchQuantityAction: (state, action: PayloadAction<MotiliLineItem>) => {
            const changeItem = action.payload;
            const vendorItem = state.vendorLineItems.find(
                item => item.rowId === changeItem.rowId
            );
            if (!vendorItem) return;
            state.motiliLineItems = state.motiliLineItems.map(item =>
                item.rowId === changeItem.rowId
                    ? {
                          ...changeItem,
                          quantity:
                              vendorItem.vendorQuantityOrdered ||
                              vendorItem.quantity,
                          resolved: {
                              by: 'update_qty',
                              quantity:
                                  vendorItem.vendorQuantityOrdered ||
                                  vendorItem.quantity,
                              revertIds: [changeItem.rowId],
                          },
                      }
                    : item
            );
            state.vendorLineItems = state.vendorLineItems.map(item =>
                item.rowId === changeItem.rowId
                    ? { ...item, resolved: { by: 'update_qty' } }
                    : item
            );
        },
        changeItemAction: (
            state,
            action: PayloadAction<{
                motiliItem: MotiliLineItem;
                vendorItem: VendorLineItem;
            }>
        ) => {
            const { motiliItem, vendorItem } = action.payload;
            state.motiliLineItems = state.motiliLineItems.map(item =>
                item.rowId === motiliItem.rowId && item.cost && item.price
                    ? {
                          ...item,
                          ...motiliItem,
                          vendor: true,
                          sku: motiliItem.resolved?.sku as string,
                          display: motiliItem.resolved?.display as string,
                          details: motiliItem.resolved?.details as string,
                          suppliedDescription: undefined,
                          quantity: motiliItem.resolved?.quantity as number,
                          cost: {
                              ...item.cost,
                              cost: motiliItem.resolved?.adjustedCost ?? 0,
                              estVendorCost:
                                  motiliItem.resolved?.estVendorCost ?? 0,
                          },
                          price: {
                              ...item.price,
                              adjustedPrice:
                                  motiliItem.resolved?.adjustedPrice ?? 0,
                          },
                          resolved: {
                              ...(motiliItem.resolved as ResolveValues),
                              revertIds: [item.rowId, vendorItem.rowId],
                          },
                      }
                    : item
            );
            state.vendorLineItems = state.vendorLineItems.map(item =>
                item.rowId === motiliItem.rowId
                    ? {
                          ...vendorItem,
                          rowId: item.rowId,
                          motili: true,
                          resolved: { by: 'change' },
                      }
                    : item
            );

            state.motiliLineItems = state.motiliLineItems.filter(
                item => item.rowId !== vendorItem.rowId
            );
            state.vendorLineItems = state.vendorLineItems.filter(
                item => item.rowId !== vendorItem.rowId
            );
        },
        adjustItemsAction: (
            state,
            action: PayloadAction<{
                adjustItems: Adjustment[];
                reasonId?: string;
                reasonNote?: string;
            }>
        ) => {
            const { adjustItems, reasonId, reasonNote } = action.payload;
            state.adjustReason = reasonId;
            state.adjustReasonNote = reasonNote;
            adjustItems.forEach(adj => {
                const lineItem = state.motiliLineItems.find(
                    item => item.rowId === adj.rowId
                );
                if (lineItem) {
                    lineItem.adjustment = adj;
                }
                if (lineItem && adj.suppliedDescription) {
                    lineItem.suppliedDescription = adj.suppliedDescription;
                }
                if (
                    lineItem &&
                    lineItem.cost &&
                    adj.adjustedVendorCostAdjustmentTypeId
                ) {
                    lineItem.cost.cost = adj.adjustedVendorCostAdjustment ?? 0;
                }
            });
        },
    },
    extraReducers: builder => {
        builder.addCase(fetchCompareOrder.fulfilled, (state, action) => {
            state.initialized = true;
            state.options = action.payload;
        });
    },
    selectors: {
        selectInitialized: state => state.initialized,
        selectFulfillmentDetailsDiscrepancies: state =>
            state.options?.fulfillmentDetailsDiscrepancies || false,
        selectLineItemDiscrepancies: state =>
            state.options?.lineItemDiscrepancies || false,
        selectHasQuantityDoesNotMatch: state =>
            state.options?.motiliLineItems.some(
                item => item.quantityDoesNotMatch
            ) || false,
        selectModifyAction: state => state.modifyAction,
        selectReason: state => state.reason ?? '',
        selectReasonSelection: state => state.reasonSelection,
        selectSecondaryReasonSelection: state => state.secondaryReasonSelection,
        selectFulfillmentType: state => state.fulfillmentType,
        selectMotiliLineItems: state => state.motiliLineItems || [],
        selectVendorLineItems: state => state.vendorLineItems || [],
        selectAdjustReason: state => state.adjustReason,
        selectAdjustReasonNote: state => state.adjustReasonNote,
        selectIsResolved: state =>
            state.motiliLineItems.every(
                item =>
                    (item.motili &&
                        item.vendor &&
                        !item.quantityDoesNotMatch) ||
                    item.resolved
            ),
        selectAdjustableItems: createSelector(
            (state: ModifyOrderState) => state.motiliLineItems || [],
            items =>
                items.filter(
                    item =>
                        (item.motili &&
                            item.vendor &&
                            !item.quantityDoesNotMatch) ||
                        (item.resolved && item.resolved.by !== 'delete')
                )
        ),
        selectUnmatchedVendorItems: createSelector(
            (state: ModifyOrderState) => state.vendorLineItems || [],
            items =>
                items.filter(
                    item => item.vendor && !item.motili && !item.resolved
                )
        ),
    },
});

export const {
    resetState,
    modifyChangeAction,
    reasonChangeAction,
    reasonSelectionChangeAction,
    secondaryReasonSelectionChangeAction,
    setFulfillmentTypeAction,
    resetEditingLineItems,
    deleteLineItemAction,
    revertLineItemsAction,
    selectProductAction,
    addProductAction,
    matchQuantityAction,
    changeItemAction,
    adjustItemsAction,
} = modifyOrderSlice.actions;

export const {
    selectInitialized,
    selectFulfillmentDetailsDiscrepancies,
    selectLineItemDiscrepancies,
    selectHasQuantityDoesNotMatch,
    selectModifyAction,
    selectReason,
    selectReasonSelection,
    selectSecondaryReasonSelection,
    selectFulfillmentType,
    selectMotiliLineItems,
    selectVendorLineItems,
    selectIsResolved,
    selectAdjustableItems,
    selectUnmatchedVendorItems,
    selectAdjustReason,
    selectAdjustReasonNote,
} = modifyOrderSlice.selectors;

export default modifyOrderSlice.reducer;
